export type GuideTourObserversConfig = {
  /**
   * CSS class (sem `.`) do **fundo escuro atrás do modal** neste app.
   *
   * Enquanto existir um elemento visível com essa classe, a lib entende que há
   * modal aberto — e tours com `waitForModalHidden` ficam esperando sumir.
   *
   * @example 'backdrop' no Hexa V2
   */
  modalBackdropClass?: string
  /**
   * Elementos que **são** o modal (web components, ids, tags).
   *
   * A lib sempre soma os genéricos: `dialog[open]`, `[role="dialog"]`.
   *
   * @example ['hexa-v2-dialog', 'hexa-v2-modal']
   */
  modalHostSelectors?: readonly string[]
  /**
   * Fallback: tratar overlay fixo em tela cheia com fundo escuro como modal aberto.
   *
   * **Não** é “quando a modal fecha”. É detecção extra para modais sem host
   * conhecido — um `div` fullscreen com dimming, por exemplo.
   *
   * @default true
   */
  detectFullscreenBlockingOverlay?: boolean
}

export type GuideTourUiConfig = {
  /**
   * Tempo em ms que o aviso de passo pulado permanece visível antes de sumir.
   *
   * @default 5000
   */
  skippedStepNoticeMs?: number
}

export type GuideTourEligibilityConfig = {
  /**
   * Função que informa se o usuário ainda é "novo" neste app.
   *
   * Retorno:
   * - `undefined` → ainda carregando (ex.: API). A lib **espera** e reavalia.
   * - `false` → usuário **não** é novo. Bloqueia tours automáticos (mesmo com storage limpo).
   * - `true` → pode seguir para o **storage** (`localStorage`) decidir se o tour já foi visto.
   *
   * Se omitir `isNewUser`, só o storage decide.
   */
  isNewUser?: () => boolean | undefined
}

export type GuideTourConfigInput = {
  /**
   * Pasta com os JSON dos tours, relativa a `src/tours/index.ts`.
   * @default '.'
   */
  toursRoot?: '.'
  observers?: GuideTourObserversConfig
  ui?: GuideTourUiConfig
} & GuideTourEligibilityConfig

export type ResolvedGuideTourUiConfig = {
  skippedStepNoticeMs: number
}

export type ResolvedGuideTourObserversConfig = {
  modalBackdropClass: string
  modalHostSelectors: readonly string[]
  detectFullscreenBlockingOverlay: boolean
}

export type ResolvedGuideTourConfig = {
  toursRoot: '.'
  observers: ResolvedGuideTourObserversConfig
  ui: ResolvedGuideTourUiConfig
  isNewUser?: () => boolean | undefined
}

// --- defaults ---
/** Padrões DOM genéricos — hosts específicos do app ficam no setup. */
export const DEFAULT_GUIDE_TOUR_OBSERVERS: ResolvedGuideTourObserversConfig = {
  modalBackdropClass: '',
  modalHostSelectors: ['dialog[open]', '[role="dialog"]'],
  detectFullscreenBlockingOverlay: true,
}

/** Padrões de UI — timeouts e textos podem ser sobrescritos no setup do app. */
export const DEFAULT_GUIDE_TOUR_UI = {
  skippedStepNoticeMs: 5_000,
} as const

// --- defineGuideTourConfig ---
const mergeModalHostSelectors = (
  appSelectors: readonly string[] | undefined
): readonly string[] => [
  ...new Set([
    ...(appSelectors ?? []),
    ...DEFAULT_GUIDE_TOUR_OBSERVERS.modalHostSelectors,
  ]),
]

const resolveObservers = (
  observers: GuideTourConfigInput['observers'] = {}
): ResolvedGuideTourObserversConfig => ({
  modalBackdropClass:
    observers.modalBackdropClass ??
    DEFAULT_GUIDE_TOUR_OBSERVERS.modalBackdropClass,
  modalHostSelectors: mergeModalHostSelectors(observers.modalHostSelectors),
  detectFullscreenBlockingOverlay:
    observers.detectFullscreenBlockingOverlay ??
    DEFAULT_GUIDE_TOUR_OBSERVERS.detectFullscreenBlockingOverlay,
})

const resolveUi = (
  ui: GuideTourConfigInput['ui'] = {}
): ResolvedGuideTourUiConfig => ({
  skippedStepNoticeMs:
    ui.skippedStepNoticeMs ?? DEFAULT_GUIDE_TOUR_UI.skippedStepNoticeMs,
})

export const defineGuideTourConfig = (
  config: GuideTourConfigInput = {}
): ResolvedGuideTourConfig => {
  const toursRoot = config.toursRoot ?? '.'

  if (toursRoot !== '.') {
    throw new TypeError(
      'Guide tour config must live beside the JSON folder. Use toursRoot: "."'
    )
  }

  return {
    toursRoot,
    observers: resolveObservers(config.observers),
    ui: resolveUi(config.ui),
    isNewUser: config.isNewUser,
  }
}

// --- runtime ---
let activeConfig: ResolvedGuideTourConfig | null = null

export type GuideTourUserEligibility = 'eligible' | 'blocked' | 'pending'

export const applyGuideTourRuntimeConfig = (
  config: ResolvedGuideTourConfig
): void => {
  activeConfig = config
}

export const resetGuideTourRuntimeConfig = (): void => {
  activeConfig = null
}

export const getGuideTourRuntimeConfig = (): ResolvedGuideTourConfig | null =>
  activeConfig

export const getGuideTourObserversConfig =
  (): ResolvedGuideTourObserversConfig =>
    activeConfig?.observers ?? DEFAULT_GUIDE_TOUR_OBSERVERS

export const getGuideTourSkippedStepNoticeMs = (): number =>
  activeConfig?.ui.skippedStepNoticeMs ??
  DEFAULT_GUIDE_TOUR_UI.skippedStepNoticeMs

export const getGuideTourIsNewUser = ():
  | (() => boolean | undefined)
  | undefined => activeConfig?.isNewUser

export const resolveGuideTourUserEligibility = (): GuideTourUserEligibility => {
  const isNewUser = getGuideTourIsNewUser()

  if (!isNewUser) {
    return 'eligible'
  }

  const signal = isNewUser()

  if (signal === undefined) {
    return 'pending'
  }

  if (signal === false) {
    return 'blocked'
  }

  return 'eligible'
}

/** `true` only when eligibility is resolved and the user may proceed to storage rules. */
export const isGuideTourEligibleByUserSignal = (): boolean =>
  resolveGuideTourUserEligibility() === 'eligible'

export const hasGuideTourUserEligibilitySignal = (): boolean =>
  getGuideTourIsNewUser() !== undefined
